/**
 * mdlondon Product Comparison Tool
 * Allows users to compare multiple products side by side
 */

class ProductComparisonManager {
  constructor() {
    this.storageKey = 'mdlondon_comparison';
    this.maxProducts = 4;
    this.comparisonProducts = this.loadComparison();
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.updateAllButtons();
    this.updateComparisonBar();
    this.initializeComparisonPage();
  }

  setupEventListeners() {
    // Add to comparison
    document.addEventListener('click', (e) => {
      if (e.target.closest('.compare-button')) {
        e.preventDefault();
        this.handleCompareToggle(e.target.closest('.compare-button'));
      }
    });

    // Remove from comparison
    document.addEventListener('click', (e) => {
      if (e.target.closest('.comparison-remove')) {
        e.preventDefault();
        this.handleRemove(e.target.closest('.comparison-remove'));
      }
    });

    // Clear all
    document.addEventListener('click', (e) => {
      if (e.target.closest('.comparison-clear')) {
        e.preventDefault();
        this.clearAll();
      }
    });

    // View comparison page
    document.addEventListener('click', (e) => {
      if (e.target.closest('.comparison-view')) {
        e.preventDefault();
        window.location.href = '/pages/compare';
      }
    });
  }

  handleCompareToggle(button) {
    const productId = button.dataset.productId;
    const productData = this.getProductDataFromButton(button);

    if (this.isInComparison(productId)) {
      this.removeProduct(productId);
      this.showToast(productData.title + ' removed from comparison');
    } else {
      if (this.comparisonProducts.length >= this.maxProducts) {
        this.showToast('Maximum ' + this.maxProducts + ' products can be compared', 'error');
        return;
      }
      this.addProduct(productData);
      this.showToast(productData.title + ' added to comparison');
    }

    this.updateButton(button, productId);
    this.updateAllButtons();
    this.updateComparisonBar();
  }

  handleRemove(button) {
    const productId = button.dataset.productId;
    this.removeProduct(productId);
    this.updateAllButtons();
    this.updateComparisonBar();
    this.renderComparisonPage();
  }

  getProductDataFromButton(button) {
    return {
      id: button.dataset.productId,
      title: button.dataset.productTitle,
      handle: button.dataset.productHandle,
      price: button.dataset.productPrice,
      image: button.dataset.productImage,
      url: button.dataset.productUrl || '/products/' + button.dataset.productHandle
    };
  }

  addProduct(productData) {
    // Don't add if already exists
    if (this.isInComparison(productData.id)) return;

    // Don't add if at max capacity
    if (this.comparisonProducts.length >= this.maxProducts) return;

    this.comparisonProducts.push(productData);
    this.saveComparison();

    document.dispatchEvent(new CustomEvent('comparison:added', {
      detail: productData
    }));
  }

  removeProduct(productId) {
    this.comparisonProducts = this.comparisonProducts.filter(p => p.id !== productId);
    this.saveComparison();

    document.dispatchEvent(new CustomEvent('comparison:removed', {
      detail: { productId }
    }));
  }

  clearAll() {
    this.comparisonProducts = [];
    this.saveComparison();
    this.updateAllButtons();
    this.updateComparisonBar();
    this.renderComparisonPage();

    document.dispatchEvent(new CustomEvent('comparison:cleared'));
    this.showToast('Comparison cleared');
  }

  isInComparison(productId) {
    return this.comparisonProducts.some(p => p.id === productId);
  }

  updateButton(button, productId) {
    const isInComparison = this.isInComparison(productId);

    if (isInComparison) {
      button.classList.add('compare-button--active');
      button.setAttribute('aria-pressed', 'true');
      const text = button.querySelector('.compare-button__text');
      if (text) text.textContent = 'Remove from Compare';
    } else {
      button.classList.remove('compare-button--active');
      button.setAttribute('aria-pressed', 'false');
      const text = button.querySelector('.compare-button__text');
      if (text) text.textContent = 'Add to Compare';
    }
  }

  updateAllButtons() {
    document.querySelectorAll('.compare-button').forEach(button => {
      const productId = button.dataset.productId;
      this.updateButton(button, productId);
    });
  }

  updateComparisonBar() {
    const bar = document.querySelector('.comparison-bar');
    if (!bar) return;

    const count = this.comparisonProducts.length;

    if (count === 0) {
      bar.classList.remove('comparison-bar--visible');
      return;
    }

    bar.classList.add('comparison-bar--visible');

    const countElement = bar.querySelector('.comparison-bar__count');
    if (countElement) {
      countElement.textContent = count + ' product' + (count !== 1 ? 's' : '') + ' to compare';
    }

    const itemsContainer = bar.querySelector('.comparison-bar__items');
    if (itemsContainer) {
      itemsContainer.innerHTML = this.comparisonProducts.map(product =>
        '<div class="comparison-bar__item">' +
          '<img src="' + this.getImageUrl(product.image, 60) + '" alt="' + this.escapeHtml(product.title) + '">' +
          '<button class="comparison-remove" data-product-id="' + product.id + '" aria-label="Remove">&times;</button>' +
        '</div>'
      ).join('');
    }
  }

  initializeComparisonPage() {
    const comparisonPage = document.querySelector('[data-comparison-page]');
    if (!comparisonPage) return;

    this.renderComparisonPage();
  }

  renderComparisonPage() {
    const comparisonPage = document.querySelector('[data-comparison-page]');
    if (!comparisonPage) return;

    if (this.comparisonProducts.length === 0) {
      this.renderEmptyComparison(comparisonPage);
      return;
    }

    this.loadProductDetails(this.comparisonProducts).then(products => {
      this.renderComparisonTable(comparisonPage, products);
    });
  }

  renderEmptyComparison(container) {
    container.innerHTML = '<div class="comparison-empty">' +
      '<h2>No Products to Compare</h2>' +
      '<p>Add products to your comparison list to see them here.</p>' +
      '<a href="/collections/all" class="button">Browse Products</a>' +
    '</div>';
  }

  async loadProductDetails(products) {
    const promises = products.map(product =>
      fetch('/products/' + product.handle + '.js')
        .then(res => res.json())
        .catch(err => {
          console.error('Error loading product:', err);
          return null;
        })
    );

    const results = await Promise.all(promises);
    return results.filter(p => p !== null);
  }

  renderComparisonTable(container, products) {
    if (products.length === 0) {
      this.renderEmptyComparison(container);
      return;
    }

    let html = '<div class="comparison-header">' +
      '<h1>Compare Products</h1>' +
      '<button class="comparison-clear">Clear All</button>' +
    '</div>';

    html += '<div class="comparison-table">';

    // Product images and titles
    html += '<div class="comparison-row comparison-row--products">' +
      '<div class="comparison-cell comparison-cell--label"></div>';

    products.forEach(product => {
      const image = product.featured_image || product.images[0];
      html += '<div class="comparison-cell comparison-cell--product">' +
        '<button class="comparison-remove" data-product-id="' + product.id + '">&times;</button>' +
        '<a href="' + product.url + '">' +
          '<img src="' + this.getImageUrl(image, 300) + '" alt="' + this.escapeHtml(product.title) + '">' +
          '<h3>' + this.escapeHtml(product.title) + '</h3>' +
        '</a>' +
      '</div>';
    });

    html += '</div>';

    // Price
    html += '<div class="comparison-row">' +
      '<div class="comparison-cell comparison-cell--label">Price</div>';
    products.forEach(product => {
      const variant = product.variants[0];
      html += '<div class="comparison-cell">' + this.formatMoney(variant.price) + '</div>';
    });
    html += '</div>';

    // Availability
    html += '<div class="comparison-row">' +
      '<div class="comparison-cell comparison-cell--label">Availability</div>';
    products.forEach(product => {
      html += '<div class="comparison-cell">' +
        (product.available ? '<span class="status-badge status-badge--available">In Stock</span>' : '<span class="status-badge status-badge--unavailable">Out of Stock</span>') +
      '</div>';
    });
    html += '</div>';

    // Vendor
    html += '<div class="comparison-row">' +
      '<div class="comparison-cell comparison-cell--label">Brand</div>';
    products.forEach(product => {
      html += '<div class="comparison-cell">' + (product.vendor || '-') + '</div>';
    });
    html += '</div>';

    // Type
    html += '<div class="comparison-row">' +
      '<div class="comparison-cell comparison-cell--label">Type</div>';
    products.forEach(product => {
      html += '<div class="comparison-cell">' + (product.type || '-') + '</div>';
    });
    html += '</div>';

    // Options (sizes, colors, etc.)
    const allOptions = this.getAllOptions(products);
    allOptions.forEach(optionName => {
      html += '<div class="comparison-row">' +
        '<div class="comparison-cell comparison-cell--label">' + optionName + '</div>';
      products.forEach(product => {
        const option = product.options.find(opt => opt.name === optionName);
        const values = option ? option.values.join(', ') : '-';
        html += '<div class="comparison-cell">' + values + '</div>';
      });
      html += '</div>';
    });

    // Add to cart buttons
    html += '<div class="comparison-row comparison-row--actions">' +
      '<div class="comparison-cell comparison-cell--label"></div>';
    products.forEach(product => {
      const variant = product.variants[0];
      html += '<div class="comparison-cell">' +
        (product.available ?
          '<button class="button button--primary" onclick="window.location.href=\'' + product.url + '\'">View Product</button>' :
          '<button class="button button--secondary" disabled>Out of Stock</button>'
        ) +
      '</div>';
    });
    html += '</div>';

    html += '</div>';

    container.innerHTML = html;
  }

  getAllOptions(products) {
    const optionNames = new Set();
    products.forEach(product => {
      product.options.forEach(option => {
        if (option.name !== 'Title') {
          optionNames.add(option.name);
        }
      });
    });
    return Array.from(optionNames);
  }

  getImageUrl(imageUrl, size = 400) {
    if (!imageUrl) return '';

    if (typeof imageUrl === 'object' && imageUrl.src) {
      imageUrl = imageUrl.src;
    }

    if (imageUrl.includes('cdn.shopify.com')) {
      const parts = imageUrl.split('?');
      const baseUrl = parts[0];
      const urlWithoutSize = baseUrl.replace(/_\d+x\d+/, '');
      const extension = urlWithoutSize.substring(urlWithoutSize.lastIndexOf('.'));
      const urlWithoutExtension = urlWithoutSize.substring(0, urlWithoutSize.lastIndexOf('.'));
      return urlWithoutExtension + '_' + size + 'x' + size + extension;
    }

    return imageUrl;
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return '£' + amount;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  showToast(message, type = 'info') {
    const toast = document.createElement('div');
    toast.className = 'comparison-toast comparison-toast--' + type;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add('comparison-toast--visible'), 10);
    setTimeout(() => {
      toast.classList.remove('comparison-toast--visible');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  loadComparison() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading comparison:', error);
      return [];
    }
  }

  saveComparison() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.comparisonProducts));
    } catch (error) {
      console.error('Error saving comparison:', error);
    }
  }

  // Public API
  getComparisonProducts() {
    return [...this.comparisonProducts];
  }

  getComparisonCount() {
    return this.comparisonProducts.length;
  }
}

// Initialize
const comparisonManager = new ProductComparisonManager();
window.ProductComparisonManager = comparisonManager;
